import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Typography, Box, Grid, Card, CardContent, CardMedia, IconButton, Modal, Button } from '@mui/material';
import { Image as ImageIcon, PictureAsPdf as PictureAsPdfIcon, Article as ArticleIcon, InsertDriveFile as InsertDriveFileIcon, VideoFile as VideoFileIcon, Close as CloseIcon } from '@mui/icons-material';
export const AttachmentGallery = ({ attachments, title }) => {
    var _a, _b, _c, _d;
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    // Ensure we have valid attachments to display
    const validAttachments = Array.isArray(attachments)
        ? attachments.filter(att => att && typeof att.url === "string")
        : [];
    // If we have no valid attachments, don't render anything
    if (validAttachments.length === 0) {
        return null;
    }
    const getFileTypeIcon = (fileType) => {
        if (fileType.startsWith('image/'))
            return _jsx(ImageIcon, {});
        if (fileType.startsWith('video/'))
            return _jsx(VideoFileIcon, {});
        if (fileType === 'application/pdf')
            return _jsx(PictureAsPdfIcon, {});
        if (fileType.includes('document') || fileType.includes('text'))
            return _jsx(ArticleIcon, {});
        return _jsx(InsertDriveFileIcon, {});
    };
    const getFileTypeLabel = (fileType) => {
        var _a;
        if (fileType.startsWith('image/'))
            return fileType.split('/')[1].toUpperCase();
        if (fileType.startsWith('video/'))
            return fileType.split('/')[1].toUpperCase();
        if (fileType === 'application/pdf')
            return 'PDF';
        if (fileType.includes('document'))
            return 'DOC';
        if (fileType.includes('text'))
            return 'TXT';
        return ((_a = fileType.split('/').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'FILE';
    };
    // Check if attachment is an image or video
    const isImage = (fileType) => fileType.startsWith('image/');
    const isVideo = (fileType) => fileType.startsWith('video/');
    return (_jsxs(Box, { sx: { width: '100%', mb: 2 }, children: [title && (_jsxs(Typography, { variant: "subtitle1", sx: { fontWeight: 600, mb: 1 }, children: [title, ":"] })), _jsx(Grid, { container: true, spacing: 1, children: validAttachments.map((attachment, index) => (_jsx(Grid, { item: true, xs: 6, sm: 4, md: 3, sx: { minWidth: '140px' }, children: _jsxs(Card, { sx: {
                            cursor: "pointer",
                            transition: "transform 0.3s",
                            minWidth: '100%',
                            "&:hover": {
                                transform: "scale(1.02)",
                                boxShadow: 3,
                            },
                        }, onClick: () => setSelectedAttachment(attachment.url), children: [isImage(attachment.fileType) ? (_jsx(CardMedia, { component: "img", height: "120", image: attachment.url, alt: attachment.fileName || `Attachment ${index + 1}`, sx: { objectFit: "cover" } })) : isVideo(attachment.fileType) ? (_jsxs(Box, { sx: {
                                    height: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    minWidth: '100%'
                                }, children: [_jsx(VideoFileIcon, {}), _jsx(Typography, { variant: "caption", sx: { mt: 1 }, children: getFileTypeLabel(attachment.fileType) })] })) : (_jsxs(Box, { sx: {
                                    height: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    minWidth: '100%'
                                }, children: [getFileTypeIcon(attachment.fileType), _jsx(Typography, { variant: "caption", sx: { mt: 1 }, children: getFileTypeLabel(attachment.fileType) })] })), _jsx(CardContent, { sx: { py: 1 }, children: _jsx(Typography, { variant: "caption", noWrap: true, children: attachment.fileName || `File ${index + 1}` }) })] }) }, index))) }), _jsx(Modal, { open: selectedAttachment !== null, onClose: () => setSelectedAttachment(null), sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }, children: _jsxs(Box, { sx: {
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        outline: 'none',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 24,
                        p: 2
                    }, children: [_jsx(IconButton, { sx: { position: 'absolute', top: 8, right: 8, zIndex: 10 }, onClick: () => setSelectedAttachment(null), children: _jsx(CloseIcon, {}) }), selectedAttachment && (_jsx(_Fragment, { children: ((_a = validAttachments.find(a => a.url === selectedAttachment)) === null || _a === void 0 ? void 0 : _a.fileType.startsWith('image/')) ? (_jsx("img", { src: selectedAttachment, alt: "Selected attachment", style: { maxWidth: '100%', maxHeight: 'calc(90vh - 100px)', objectFit: 'contain' } })) : ((_b = validAttachments.find(a => a.url === selectedAttachment)) === null || _b === void 0 ? void 0 : _b.fileType.startsWith('video/')) ? (_jsx("video", { src: selectedAttachment, controls: true, style: { maxWidth: '100%', maxHeight: 'calc(90vh - 100px)' } })) : (_jsxs(Box, { sx: { textAlign: 'center', mt: 2 }, children: [_jsx(Box, { sx: { mb: 2 }, children: getFileTypeIcon(((_c = validAttachments.find(a => a.url === selectedAttachment)) === null || _c === void 0 ? void 0 : _c.fileType) || '') }), _jsx(Typography, { variant: "body1", sx: { mb: 2 }, children: ((_d = validAttachments.find(a => a.url === selectedAttachment)) === null || _d === void 0 ? void 0 : _d.fileName) || 'File' }), _jsx(Button, { variant: "contained", href: selectedAttachment, target: "_blank", rel: "noopener noreferrer", children: "Download File" })] })) }))] }) })] }));
};
export default AttachmentGallery;
